import type { Breed } from 'shared/@types';
import { getCanonicalBreedSlug } from 'shared/library/canonical-breed-page';
import { getSafeLanguageCode } from 'shared/library/i18n/languages';
import { buildRoute } from 'shared/library/routes/route';

type CanonicalBreedComparisonOptions = {
  breed: Breed;
  comparable: Breed;
  requestedBreedSlug: string;
  requestedComparableSlug: string;
  locale?: string;
  hasWebResponse: boolean;
};

type RequestedBreedComparisonOptions = Pick<
  CanonicalBreedComparisonOptions,
  'requestedBreedSlug' | 'requestedComparableSlug' | 'locale' | 'hasWebResponse'
>;

function getRequestedBreedComparisonResult({
  requestedBreedSlug,
  requestedComparableSlug,
  locale,
  hasWebResponse,
}: RequestedBreedComparisonOptions) {
  if (requestedBreedSlug !== requestedComparableSlug) {
    return null;
  }

  if (!hasWebResponse) {
    return { notFound: true as const };
  }

  return {
    redirect: {
      destination: buildRoute(
        'breeds.show',
        { breed: requestedBreedSlug },
        { lang: getSafeLanguageCode(locale) },
      ),
      statusCode: 301 as const,
    },
  };
}

function getCanonicalBreedComparisonResult({
  breed,
  comparable,
  requestedBreedSlug,
  requestedComparableSlug,
  locale,
  hasWebResponse,
}: CanonicalBreedComparisonOptions) {
  if (breed.animal !== comparable.animal) {
    return { notFound: true as const };
  }

  if (breed.id === comparable.id) {
    if (!hasWebResponse) {
      return { notFound: true as const };
    }

    return {
      redirect: {
        destination: buildRoute(
          'breeds.show',
          { breed: getCanonicalBreedSlug(breed, locale) },
          { lang: getSafeLanguageCode(locale) },
        ),
        statusCode: 301 as const,
      },
    };
  }

  if (!hasWebResponse) {
    return null;
  }

  const [firstBreed, secondBreed] =
    breed.id < comparable.id ? [breed, comparable] : [comparable, breed];
  const canonicalBreedSlug = getCanonicalBreedSlug(firstBreed, locale);
  const canonicalComparableSlug = getCanonicalBreedSlug(secondBreed, locale);

  if (
    requestedBreedSlug === canonicalBreedSlug &&
    requestedComparableSlug === canonicalComparableSlug
  ) {
    return null;
  }

  return {
    redirect: {
      destination: buildRoute(
        'breeds.show.compare.show',
        { breed: canonicalBreedSlug, comparable: canonicalComparableSlug },
        { lang: getSafeLanguageCode(locale) },
      ),
      statusCode: 301 as const,
    },
  };
}

export { getCanonicalBreedComparisonResult, getRequestedBreedComparisonResult };
